import React, { useState } from 'react';

/**
 * Kilograms or pounds, per exercise: some machines are marked in kg, some in
 * lb, so each exercise carries its own unit (ex.unit, kg when unset). Weights
 * are always stored in kg, so progress never mixes units; this converts what
 * is shown and what is typed. Tapping the unit in a weight box switches that
 * exercise only, converted in place (10 kg becomes 22 lb, nothing is emptied).
 *
 * Pounds typed are kept to three decimals of a kilo, so switching back and
 * forth gives back what was typed (22.5 lb is 10.206 kg is 22.5 lb).
 */

export const LB_PER_KG = 2.20462262;

const round = (v, places) => Math.round(v * 10 ** places) / 10 ** places;
export const unitOf = (ex) => (ex?.unit === 'lb' ? 'lb' : 'kg');
export const otherUnit = (unit) => (unit === 'lb' ? 'kg' : 'lb');
/** A kg value in a unit: pounds to 0.1, kilos to 0.01. */
export const fromKg = (kg, unit = 'kg') => (kg == null ? null : unit === 'lb' ? round(kg * LB_PER_KG, 1) : round(kg, 2));
/** A value typed in a unit, back to kg for storing. */
export const toKg = (value, unit = 'kg') => (value == null ? null : unit === 'lb' ? round(value / LB_PER_KG, 3) : value);
/** A number without trailing zeros: 20, 22.5, 44.1. */
export const plain = (v) => (v == null ? '' : String(Number(v)));
/** "20 kg" or "44.1 lb". */
export const fmtWeight = (kg, unit = 'kg') => (kg == null ? null : `${plain(fromKg(kg, unit))} ${unit}`);
/** The + and − on a weight: 1 in the machine's own unit, kg or lb alike. */
export const weightStep = () => 1;

/** The unit as a switch: "kg ⇄" / "lb ⇄". */
export function UnitSwitch({ unit, onToggle, className = '' }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle?.(); }}
      className={`text-[11px] text-gray-400 underline decoration-dotted underline-offset-2 hover:text-white ${className}`}
      title={unit === 'kg' ? 'This machine is in pounds' : 'This machine is in kilograms'}
    >
      {unit}
    </button>
  );
}

/**
 * A weight box in its exercise's unit, with the unit inside it to tap. Shows
 * the stored kg in that unit and stores what is typed as kg; while typing it
 * keeps the text as typed ("22." stays "22.").
 */
export function WeightInput({ kg, unit = 'kg', onChange, onToggleUnit, className = '', placeholder = '-' }) {
  const [text, setText] = useState(null);
  const shown = text ?? (kg == null ? '' : plain(fromKg(kg, unit)));
  return (
    <div className="relative">
      <input
        inputMode="decimal"
        value={shown}
        placeholder={placeholder}
        onFocus={() => setText(kg == null ? '' : plain(fromKg(kg, unit)))}
        onChange={(e) => {
          const clean = e.target.value.replace(/[^\d.]/g, '');
          setText(clean);
          onChange(clean === '' || clean === '.' ? null : toKg(Number(clean), unit));
        }}
        onBlur={() => setText(null)}
        className={`${className} pr-7`}
      />
      {onToggleUnit && <UnitSwitch unit={unit} onToggle={onToggleUnit} className="absolute right-1.5 top-1/2 -translate-y-1/2" />}
    </div>
  );
}
